import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { getAppVersion } from "src/services/configService";
import { LocalStorage } from "src/services/localStorageService";
import LanguageSwitcher from "src/ui/shared/LanguageSwitcher/LanguageSwitcher";
import { makeStyles } from "tss-react/mui";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import AuthService from "@services/authService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { UncontrolledTextField } from "@shared/form/TextField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { TenantSwitcher } from "./TenantSwitcher";
import { ThemeSwitcher } from "./ThemeSwitcher";

const useStyles = makeStyles()((theme: Theme) => ({
    switchLabel: {
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    switchContent: { display: "flex", flexDirection: "column" },
    settingsRows: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    settingsRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: theme.spacing(2),
    },
    controlBox: {
        width: 200,
        flexShrink: 0,
    },
    resetRow: {
        paddingTop: theme.spacing(3),
        borderTop: `1px solid ${theme.palette.emteria.border.primary}`,
    },
    resetButton: {
        flexShrink: 0,
    },
    secondPanel: {
        marginTop: theme.spacing(3),
    },
    confirmDialogParagraph: {
        marginBottom: theme.spacing(2),
    },
    confirmDialogListTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    confirmDialogList: {
        margin: 0,
        paddingLeft: theme.spacing(3),
    },
}));

type SettingsRowProps = {
    label: string;
    description: string;
    children: React.ReactNode;
};

function SettingsRow({ label, description, children }: SettingsRowProps) {
    const { classes } = useStyles();

    return (
        <Box className={classes.settingsRow}>
            <Box className={classes.switchContent}>
                <span className={classes.switchLabel}>{label}</span>
                <div>{description}</div>
            </Box>
            <Box className={classes.controlBox}>{children}</Box>
        </Box>
    );
}

function AccountPortalSettingsPage() {
    const [isResetConfirmOpen, setIsResetConfirmOpen] = React.useState(false);

    const appVersion = getAppVersion();
    const { t } = useTranslation(["common", "pages"]);
    const { classes, cx } = useStyles();

    const handleResetSettings = () => {
        LocalStorage.reset();
        showSuccessToast(t("pages:settingsPage.portalSettings.resetSettings.toastSuccess"));
        window.location.reload();
    };
    const handleResetConfirm = () => {
        handleResetSettings();
    };

    return (
        <PageContainer fullWidth={false}>
            <SectionPanel title={t("pages:settingsPage.portalSettings.title")}>
                <Box className={classes.settingsRows}>
                    <SettingsRow
                        label={t("pages:settingsPage.language.label")}
                        description={t("pages:settingsPage.language.description")}
                    >
                        <LanguageSwitcher showLabel={false} fullWidth={true} />
                    </SettingsRow>
                    <SettingsRow
                        label={t("pages:settingsPage.portalSettings.themeSwitch.label")}
                        description={t("pages:settingsPage.portalSettings.themeSwitch.description")}
                    >
                        <ThemeSwitcher />
                    </SettingsRow>
                    <Box className={cx(classes.settingsRow, classes.resetRow)}>
                        <div>{t("pages:settingsPage.portalSettings.resetSettings.description")}</div>
                        <DangerOutlinedButton
                            startIcon={<i className="fa-solid fa-fw fa-arrow-rotate-left" />}
                            onClick={() => setIsResetConfirmOpen(true)}
                            name="reset-settings"
                            className={classes.resetButton}
                        >
                            {t("pages:settingsPage.portalSettings.resetSettings.button")}
                        </DangerOutlinedButton>
                    </Box>
                </Box>
            </SectionPanel>

            <SectionPanel title={t("pages:settingsPage.portalSettings.technicalDetails.title")} className={classes.secondPanel}>
                <div>{t("pages:settingsPage.portalSettings.technicalDetails.description")}</div>
                <Box className={classes.settingsRows}>
                    <SettingsRow
                        label={t("pages:settingsPage.portalSettings.version.label")}
                        description={t("pages:settingsPage.portalSettings.version.description")}
                    >
                        <UncontrolledTextField
                            fullWidth
                            value={appVersion ?? t("common:status.unknown")}
                            disabled
                            slotProps={{
                                htmlInput: { "aria-label": t("pages:settingsPage.portalSettings.version.label") },
                            }}
                        />
                    </SettingsRow>
                    {AuthService.mayUserSeeTenantSwitcher() ? (
                        <SettingsRow
                            label={t("pages:settingsPage.portalSettings.tenantSwitch.label")}
                            description={t("pages:settingsPage.portalSettings.tenantSwitch.description")}
                        >
                            <TenantSwitcher />
                        </SettingsRow>
                    ) : null}
                </Box>
            </SectionPanel>

            <ConfirmDialog
                open={isResetConfirmOpen}
                onClose={() => setIsResetConfirmOpen(false)}
                onConfirm={handleResetConfirm}
                title={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.title")}
                primaryActionText={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.confirmButton")}
                dangerButton
            >
                <Box component="p" className={classes.confirmDialogParagraph}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.description")}
                </Box>
                <Box component="p" className={classes.confirmDialogListTitle}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeReset")}
                </Box>
                <Box component="ul" className={classes.confirmDialogList}>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.languageAndTheme")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.uiPreferences")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.tableSettings")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.collapsedSections")}
                    </li>
                </Box>
            </ConfirmDialog>
        </PageContainer>
    );
}

export default withMetaDescription(AccountPortalSettingsPage, {
    title: i18next.t("pages:settingsPage.pages.portal.metaTitle"),
});
